import { useAppDispatch } from "../../../app/hooks";
import { useSelector } from "react-redux";
import { classroomStudentsSlice, selectClassroomStudents } from "../../../redux/classroom/classroomStudentsSlice";
import { classroomDeleteStudent } from "../../../redux/classroom/classroomRemoveStudentSlice";
import PrizaAvatarGrid, { PrizaAvatarGridItem } from "../../../features/base/grid/PrizaAvatarGrid";
import { useEffect, useMemo, useState } from "react";
import { Box, Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from "@mui/material";
import { NamedObject } from "../../../redux/object/namedObject";

import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import BadgedAvatar from "../../../features/base/BadgedAvatar";
import { assertThumbnailUrl, isoToBrt } from "../../../redux/common";

import GridOnIcon from '@mui/icons-material/GridOn';
import DnsIcon from '@mui/icons-material/Dns';
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { routeStudentNew } from "../../../routes/student";

interface ExistingStudentsListProps {
    schoolId: string;
    classroomId: string;
}

const ExistingStudentsList = ({ schoolId, classroomId }: ExistingStudentsListProps) => {
    const dispatch = useAppDispatch();
    const classroomStudents = useSelector(selectClassroomStudents);
    const [table, setTable] = useState(true);
    const navigate = useNavigate();

    const request = useMemo(() => ({ schoolId, classroomId }), [schoolId, classroomId]);
    useEffect(() => {
        dispatch(classroomStudentsSlice.actions.check(request));
    }, [classroomStudents, request]);

    const list = classroomStudents.list || [];
    const mapped: PrizaAvatarGridItem[] = list.map((item) => {
        const obj = NamedObject.student(item);
        return {
            image: obj.thumbnail,
            title: obj.display,
            subtitle: item.name,
            extra: <Tooltip title="Remover aluno da sala de aula">
                <Box sx={{ display: "flex", flexDirection: "row", gap: 1 }}>
                    <Button variant="outlined" size="small" color='error' onClick={() => {
                        dispatch(classroomDeleteStudent({ classroomId: classroomId, student: item }));
                    }}>
                        <PersonRemoveIcon />
                    </Button>
                </Box>
            </Tooltip>,
        } as PrizaAvatarGridItem;
    });

    const buildContent = !table ? undefined : () => {
        if (list.length == 0) {
            return <Box sx={{ p: 2 }}>
                <Typography variant="body1" component="div">
                    Nenhum aluno associado a esta sala de aula.
                </Typography>
            </Box>;
        }
        return <>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Aluno</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Nome</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Nascimento</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Matricula</TableCell>
                        <TableCell align="left" sx={{
                                color: (theme) => theme.palette.primary.contrastText,
                                backgroundColor: (theme) => theme.palette.primary.main,
                        }}>Remover</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {list.map((item) => {
                        const sx = {
                            padding: "4px 8px",
                        };
                        return <TableRow key={item.id} hover>
                            <TableCell sx={sx}>
                                <Stack direction="row" alignItems="center">
                                    <BadgedAvatar
                                        title={item.name}
                                        avatar={assertThumbnailUrl(item.url)}
                                        size="32px"
                                        marginRight={1}
                                    />
                                    {item.name}
                                </Stack>
                            </TableCell>
                            <TableCell sx={sx}>{item.nickname}</TableCell>
                            <TableCell sx={sx}>{isoToBrt(item.birthDate)}</TableCell>
                            <TableCell sx={sx}>{item.schoolNumber}</TableCell>
                            <TableCell sx={sx}>
                                <Button variant="outlined" size="small" color='error' onClick={() => {
                                    dispatch(classroomDeleteStudent({ classroomId: classroomId, student: item }));
                                }}>
                                    <PersonRemoveIcon />
                                </Button>
                            </TableCell>
                        </TableRow>;
                    })}
                </TableBody>
            </Table>
        </>;
    };

    const addStudent = () => {
        console.log("New student");
        navigate(routeStudentNew(schoolId, classroomId));
    };

    const buttons = [];
    buttons.push({
        label: table ? <DnsIcon /> : <GridOnIcon />,
        click: () => {
            setTable(!table);
        }
    });
    buttons.push({
        label: <Tooltip title="Adicionar novo aluno">
            <Stack direction="row" alignItems="center" spacing={1}>
                <AddIcon />
                Novo
            </Stack>
        </Tooltip>,
        click: () => {
            addStudent();
        }
    });

    return <PrizaAvatarGrid list={mapped}
        toolbar={{
            label: "Alunos da Sala de Aula (" + mapped.length + ")",
            buttons: buttons
        }}
        state={classroomStudents.state}
        retry={() => { dispatch(classroomStudentsSlice.actions.clean()); }}
        buildContent={buildContent}
        messages={{
            empty: "Nenhum aluno associado a esta sala de aula.",
            error: "Erro ao carregar alunos."
        }}
    />;

};

export default ExistingStudentsList;